import React, { useContext, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PhoneInput from 'react-phone-input-2';
import { isValidPhoneNumber } from 'libphonenumber-js/mobile';
import BookerContext from '../../context/bookerContext';
import BookerShortContext from '../../context/bookerShortContext';
import { useValidatedInput } from '../../hooks/useValidatedInput';
import { isSafari } from '../../utils/getUserAgent';
import { setNativeInputValue } from '../../utils/setNativeInputValue';
import { formatPhoneNumber } from '../../utils/formatPhoneNumber';
import fieldParams from '../../const/fieldParams';
import 'react-phone-input-2/lib/style.css';
import '../../assets/styles/phone-input.sass';

const PhoneNumberInput = ({ fieldKey }) => {
  const { key, label, placeholder, required, autocomplete, errorMessage } = fieldParams[fieldKey];
  const context = useContext(BookerContext);
  const { actionBtn } = useContext(BookerShortContext);
  const {
    state: { error },
  } = context;
  const { number = '', country = 'US', dial = '1', formatted = '+1' } = useSelector(({ phone }) => phone || {});
  const dispatch = useDispatch();

  const [isSaf, setIsSaf] = useState(false);
  const [value, setValue] = useState(number);
  const [countryState, setCountryState] = useState(country);
  const [dialState, setDialState] = useState(dial);
  const [formattedState, setFormattedState] = useState(formatted);
  const [formatState, setFormatState] = useState('');
  const [er, setEr] = useState(number ? !isValidPhoneNumber(number, country) : false);

  const [setIsValid] = useValidatedInput('btn-view', isValidPhoneNumber(number, country));

  const inputRef = useRef(null);

  useEffect(() => {
    setIsSaf(isSafari());
  }, [isSaf]);

  useEffect(() => {
    if (!(value && actionBtn)) return;

    const onKeyDownHandler = event => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter' || event.keyCode === 13) {
        event.preventDefault();
        const { target } = event || {};
        const isCountryChanged = target?.classList.contains('search-box') || target?.classList.contains('country-list');
        if (isCountryChanged) return;

        actionBtn.current?.click();
      }
    };
    document.addEventListener('keydown', onKeyDownHandler);

    return () => {
      document.removeEventListener('keydown', onKeyDownHandler);
    };
  }, [value, actionBtn]);

  const onFocus = (e, data) => {
    const { format } = data || {};
    setFormatState(format);
  };

  const onChange = (inputNumber, data, e, formattedValue) => {
    const { nativeEvent, target } = e || {};
    const { countryCode, format } = data || {};
    let { dialCode } = data || {};
    let country = countryCode.toUpperCase() || 'US';
    let isValid = isValidPhoneNumber(inputNumber, country);
    const digitAmount = format.match(/\./g).length;
    const typeOfEvent = Object.prototype.toString.call(nativeEvent);

    const rollbackInputValue = value => {
      const event = new Event('input', { bubbles: true });
      setNativeInputValue(inputRef.current, value);
      inputRef.current.dispatchEvent(event);
    };

    if (
      // safari and android autocomplete events
      (typeOfEvent === '[object Event]' && nativeEvent?.composed && nativeEvent?.type === 'input') ||
      (typeOfEvent === '[object CustomEvent]' && !nativeEvent?.isTrusted)
    ) {
      if (isSaf) country = countryState;
      isValid = inputNumber === dialCode ? true : isValidPhoneNumber(inputNumber, country);

      if (isSaf || !isValid) {
        inputNumber = `${dialState}${inputNumber}`;
        formattedValue = formatPhoneNumber(formatState, inputNumber);
        dialCode = dialState;
        country = countryState;

        if (formattedValue) {
          setTimeout(() => {
            rollbackInputValue(formattedValue);
          }, 0);
        }
      }
    } else {
      const isCountryChanged =
        target?.classList.contains('search-box') ||
        target?.classList.contains('country-list') ||
        target?.parentElement?.classList.contains('country') ||
        target?.parentElement?.classList.contains('country-list');
      if (
        // manual editing
        !(
          ((typeOfEvent === '[object MouseEvent]' || typeOfEvent === '[object PointerEvent]') &&
            nativeEvent?.isTrusted &&
            e?.type === 'click') ||
          isCountryChanged
        )
      ) {
        const fs = formattedState.slice(1, dialState.length + 1);
        const fv = formattedValue.slice(1, dialState.length + 1);
        if (dialCode !== dialState || !inputNumber?.length || fs !== fv) {
          setTimeout(() => {
            rollbackInputValue(formattedState);
          }, 0);
          return;
        }
      }
    }
    setValue(formattedValue);
    setIsValid(isValid);
    setCountryState(country);
    setDialState(dialCode);
    setFormattedState(formattedValue);
    setFormatState(format);
    setEr(!isValid ? inputNumber.length === digitAmount : false);
    dispatch({
      type: 'updatePhone',
      phone:
        inputNumber && (inputNumber !== dialCode || country !== countryState)
          ? { number: inputNumber, formatted: formattedValue, country, dial: dialCode, isValid }
          : '',
    });
  };

  return (
    <div className={`form-group form-group-${key}${value && (error === key || er) ? ' error-group' : ''}`}>
      <label htmlFor={key} className={`form-label${value === '' ? ' hidden' : ''}`}>
        {label}
      </label>
      <PhoneInput
        containerClass={`form-input-container ${key}`}
        inputClass={`form-input ${key}`}
        buttonClass="dropdown-btn"
        dropdownClass="dropdown-container"
        searchClass="search-field"
        country="us"
        preferredCountries={['us']}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        enableSearch={true}
        disableSearchIcon={true}
        countryCodeEditable={true}
        {...(placeholder && { placeholder: placeholder })}
        inputProps={{
          name: key,
          ref: inputRef,
          ...(required && { required: true }),
          ...(autocomplete && { autoComplete: autocomplete }),
        }}
      />
      {value && (error === key || er) && <p className={'error'}>{errorMessage}</p>}
    </div>
  );
};

export default PhoneNumberInput;
